import {
  IMembershipExtensionPostBody,
  IMembershipExtensionUpdateProps,
} from "@/api_framework/api_modals/MembershipExtension";
import Input from "@/app/components/ui/Inputs/Input";
import MultiSelectDropdown from "@/app/components/ui/SelectMenus/MultiSelectDropdown";
import SearchableMultiSelectMenu from "@/app/components/ui/SelectMenus/MultiSelectMenu";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import { useMembershipCouponAPI } from "@/app/hooks/api_hooks/Membership/useMembershipCouponAPI";
import { useMembershipVoucherListingAPI } from "@/app/hooks/api_hooks/Membership/useMembershipVoucherListingAPI";
import { useCreateMembershipExtensionAPI } from "@/app/hooks/api_hooks/MembershipExtension/useCreateMembershipExtensionAPI";
import { useMembershipExtensionConnectedMembershipsAPI } from "@/app/hooks/api_hooks/MembershipExtension/useMembershipExtensionConnectedMembershipsAPI";
import { useMembershipExtensionDetailsAPI } from "@/app/hooks/api_hooks/MembershipExtension/useMembershipExtensionDetailsAPI";
import { useMembershipExtensionSubServicesAPI } from "@/app/hooks/api_hooks/MembershipExtension/useMembershipExtensionSubServicesAPI";
import { useUpdateMembershipExtensionAPI } from "@/app/hooks/api_hooks/MembershipExtension/useUpdateMembershipExtensionAPI";
import { useMembershipVMSCategoryAPI } from "@/app/hooks/api_hooks/Membership/useMembershipVMSCategoryAPI";
import { IClickLogBody, useClickLogAPI } from "@/app/hooks/api_hooks/org/useClickLogAPI";
import useFormValidations from "@/app/hooks/useFormValidations";
import { useRouter } from "@/app/hooks/useRouter";
import { useSelectMenuReducer, useSelectMenuReducer as reduceSelectMenu } from "@/app/hooks/useSelectMenuReducer";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import { Switch } from "@headlessui/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";
import {
  MEMBERSHIP_EXTENSION_CLICK_EVENTS,
  MEMBERSHIP_EXTENSION_SCREEN_IDS,
  MembershipExtensionClickEvent,
  buildMembershipExtensionClickLog,
  MEMBERSHIP_EXTENSION_DEFAULT_DAYS,
  isMembershipExtensionActive,
  sanitizeAmountInput,
  // sanitizeExtensionDaysInput, // re-enable with the Extension Days input below
  toMembershipExtensionStatus,
} from "../utils";

export interface IMembershipExtensionFormProps {
  extension_name: string;
  category: string;
  category_id: string;
  amount: number;
  // extension_days: number; // Extension Days input is commented out for now
  sub_services: Array<{ id: string; name: string }>;
  // Registered by the sub services MultiSelectDropdown purely so its "at least one"
  // validation can block submit; sub_services stays the source of truth.
  sub_service_ids: string[];
  connected_memberships: Array<{ id: string; name: string }>;
  extension_coupons: Array<{ coupon_id: string; coupon_name: string }>;
  coupon_voucher_mappings: Array<{
    coupon_id: string;
    coupon_name: string;
    voucher_family_id?: string;
  }>;
  status: boolean;
  is_cart_search: boolean;
}

const VOUCHER_FALLBACK_KEY = "ALL";

const MembershipExtensionCreationComponent = () => {
  const navigate = useNavigate();
  const { getRouteKey } = useRouter();
  const [params, _setParams] = useSearchParams();
  const extension_id = params.get("extension_id");

  // forExtensionDays goes back in the destructure with the Extension Days input
  const { forNumericWithDecimalAndNotStartFromZero } = useFormValidations();

  // Category & Sub Services
  const { execute: fetchCategory, membershipCategory } = useMembershipVMSCategoryAPI();
  const { execute: fetchSubServices, subServices } = useMembershipExtensionSubServicesAPI();
  const membershipCategoryItems = useSelectMenuReducer(membershipCategory, "category_name", "id");
  const subServiceItems = useSelectMenuReducer(subServices, "sub_service_name", "sub_service_id");

  // Connected Memberships
  const { execute: fetchConnectedMemberships, connectedMemberships } = useMembershipExtensionConnectedMembershipsAPI();
  const membershipItems = useSelectMenuReducer(connectedMemberships, "membership_name", "membership_id");

  // Coupons & Voucher Families
  const { execute: fetchMembershipCoupon, membershipCouponList } = useMembershipCouponAPI();
  const { execute: fetchVoucherList } = useMembershipVoucherListingAPI();
  const [voucherFamiliesByServiceType, setVoucherFamiliesByServiceType] = useState<Record<string, any[]>>({});

  const exhaustibleCouponOptions = [
    ...(membershipCouponList?.exhaustible_coupons?.map((coupon) => ({
      id: coupon.coupon_id,
      title: coupon.coupon_name,
    })) || []),
  ];

  // APIs
  const { execute: createMembershipExtension } = useCreateMembershipExtensionAPI();
  const { execute: updateMembershipExtension } = useUpdateMembershipExtensionAPI();
  const { execute: fetchMembershipExtensionDetails, membershipExtensionDetails } = useMembershipExtensionDetailsAPI();

  const formHook = useForm<IMembershipExtensionFormProps>({
    mode: "onChange",
    defaultValues: {
      status: true,
      is_cart_search: false,
      category: null,
      category_id: null,
      sub_services: [],
      sub_service_ids: [],
      connected_memberships: [],
      extension_coupons: [],
      coupon_voucher_mappings: [],
    },
  });

  const { control } = formHook;
  const { fields: mappingFields, replace: replaceMappings } = useFieldArray({
    control,
    name: "coupon_voucher_mappings",
  });

  useEffect(() => {
    fetchCategory();
    fetchSubServices();
    fetchMembershipCoupon();
    fetchConnectedMemberships();
  }, []);

  useEffect(() => {
    if (extension_id) {
      fetchMembershipExtensionDetails(extension_id);
    }
  }, [extension_id]);

  // Prefetch fallback and fetch voucher families for each selected coupon type
  useEffect(() => {
    const selectedCoupons = formHook.watch("extension_coupons") ?? [];
    const allCoupons = membershipCouponList?.exhaustible_coupons ?? [];
    const neededTypes = Array.from(
      new Set(
        selectedCoupons
          .map((c) => allCoupons.find((ac) => ac.coupon_id === c.coupon_id)?.coupon_type)
          .filter((ct) => !!ct) as string[],
      ),
    );

    (async () => {
      if (!voucherFamiliesByServiceType[VOUCHER_FALLBACK_KEY]) {
        const data = await fetchVoucherList();
        setVoucherFamiliesByServiceType((prev) => ({ ...prev, [VOUCHER_FALLBACK_KEY]: data ?? [] }));
      }
    })();

    neededTypes.forEach(async (serviceType) => {
      if (!voucherFamiliesByServiceType[serviceType]) {
        const data = await fetchVoucherList(serviceType);
        setVoucherFamiliesByServiceType((prev) => ({ ...prev, [serviceType]: data ?? [] }));
      }
    });
  }, [formHook.watch("extension_coupons"), membershipCouponList]);

  // Keep coupon-voucher mapping in sync with the selected coupons
  useEffect(() => {
    const selectedCoupons = formHook.watch("extension_coupons") ?? [];
    const existingMappings = formHook.getValues("coupon_voucher_mappings") ?? [];
    const newMappings = selectedCoupons.map((c) => {
      const existing = existingMappings.find((m) => m.coupon_id === c.coupon_id);
      return {
        coupon_id: c.coupon_id,
        coupon_name: c.coupon_name,
        voucher_family_id: existing?.voucher_family_id,
      };
    });
    replaceMappings(newMappings);
  }, [formHook.watch("extension_coupons")]);

  // Prefill the form on edit
  useEffect(() => {
    if (!membershipExtensionDetails) return;

    formHook.setValue("extension_name", membershipExtensionDetails?.extension_name);
    formHook.setValue("category", membershipExtensionDetails?.category);
    if (membershipExtensionDetails?.category_id) {
      formHook.setValue("category_id", membershipExtensionDetails?.category_id);
    }
    formHook.setValue("amount", membershipExtensionDetails?.amount);
    // formHook.setValue("extension_days", membershipExtensionDetails?.extension_days);
    formHook.setValue("status", isMembershipExtensionActive(membershipExtensionDetails?.status));
    formHook.setValue("is_cart_search", Boolean(membershipExtensionDetails?.is_cart_search));

    const selectedCoupons =
      membershipExtensionDetails?.coupons_details?.exhaustible_coupons?.map((coupon) => ({
        coupon_id: coupon?.coupon_id,
        coupon_name:
          membershipCouponList?.exhaustible_coupons?.find((ac) => ac.coupon_id === coupon?.coupon_id)?.coupon_name ?? "",
      })) || [];
    formHook.setValue("extension_coupons", selectedCoupons);
    replaceMappings(
      membershipExtensionDetails?.coupons_details?.exhaustible_coupons?.map((coupon) => ({
        coupon_id: coupon?.coupon_id,
        coupon_name:
          membershipCouponList?.exhaustible_coupons?.find((ac) => ac.coupon_id === coupon?.coupon_id)?.coupon_name ?? "",
        voucher_family_id: coupon?.voucher_family_id,
      })) || [],
    );
  }, [membershipExtensionDetails, membershipCouponList]);

  // Resolve the category id from the category name so sub services can be fetched on edit
  useEffect(() => {
    const categoryName = formHook.watch("category");
    if (!categoryName || formHook.watch("category_id")) return;
    const matchedCategory = membershipCategory?.find((category) => category?.category_name === categoryName);
    if (matchedCategory) {
      formHook.setValue("category_id", matchedCategory?.id);
    }
  }, [formHook.watch("category"), membershipCategory]);

  // Map the sub service ids coming from the detail API to their names once the options are available
  useEffect(() => {
    const selectedSubServiceIds =
      membershipExtensionDetails?.sub_service_ids ??
      membershipExtensionDetails?.sub_category_ids ??
      [];
    if (selectedSubServiceIds?.length === 0 || subServices?.length === 0) return;
    const selectedSubServices = selectedSubServiceIds
      .map((subServiceId) => {
        const matched = subServices?.find((subService) => subService?.sub_service_id === subServiceId);
        return matched ? { id: matched?.sub_service_id, name: matched?.sub_service_name } : null;
      })
      .filter(Boolean);
    formHook.setValue("sub_services", selectedSubServices);
  }, [membershipExtensionDetails, subServices]);

  // Selected memberships are stored as ids by the API, names come from the connected memberships list
  useEffect(() => {
    const selectedMembershipIds = membershipExtensionDetails?.connected_memberships ?? [];
    if (selectedMembershipIds?.length === 0 || !connectedMemberships?.length) return;
    const selectedMemberships = selectedMembershipIds
      .map((membershipId) => {
        const matched = connectedMemberships?.find((membership) => membership?.membership_id === membershipId);
        return matched ? { id: matched?.membership_id, name: matched?.membership_name } : null;
      })
      .filter(Boolean);
    formHook.setValue("connected_memberships", selectedMemberships);
  }, [membershipExtensionDetails, connectedMemberships]);

  const { execute: logClickEvent } = useClickLogAPI();
  const vmsUserId = useAppSelector((state) => state.auth?.user?.email);
  const { mappedClinicId, mappedClinicName } = useAppSelector((state) => state.root.UIStates);

  const logExtensionEvent = (eventType: MembershipExtensionClickEvent, extra: Partial<IClickLogBody> = {}) => {
    logClickEvent(
      buildMembershipExtensionClickLog(
        eventType,
        MEMBERSHIP_EXTENSION_SCREEN_IDS.CREATION,
        { clinic_id: mappedClinicId, clinic_name: mappedClinicName, vms_user_id: vmsUserId },
        { extension_id: extension_id ?? undefined, ...extra },
      ),
    );
  };

  // handleSelectSubServices must not depend on logExtensionEvent (recreated every
  // render), so it reaches the latest one through a ref.
  const logExtensionEventRef = useRef(logExtensionEvent);
  logExtensionEventRef.current = logExtensionEvent;

  // MultiSelectDropdown re-fires onSelectItem whenever the callback identity changes,
  // so the handler has to stay referentially stable. The option list is read through a
  // ref instead of a dependency to keep it that way.
  const subServiceItemsRef = useRef(subServiceItems);
  subServiceItemsRef.current = subServiceItems;

  const subServiceDefaultSelectedIds = useMemo(
    () => formHook.watch("sub_services")?.map((subService) => subService?.id) ?? [],
    [formHook.watch("sub_services")],
  );

  const handleSelectSubServices = useCallback(
    (ids: string[]) => {
      const selectedSubServices = (ids ?? []).map((id) => {
        const matched = subServiceItemsRef.current?.find((item) => item?.id === id);
        return { id, name: matched?.title ?? "" };
      });
      const previousIds = formHook.getValues("sub_services")?.map((subService) => subService?.id) ?? [];
      // Nothing changed (the dropdown replays its selection on mount/re-render) — skip
      // the setValue so the watch above doesn't spin the render loop.
      if (previousIds.length === selectedSubServices.length && previousIds.every((id) => ids?.includes(id))) {
        return;
      }
      formHook.setValue("sub_services", selectedSubServices);
      formHook.setValue("sub_service_ids", ids ?? []);
      formHook.clearErrors("sub_service_ids");
      logExtensionEventRef.current(MEMBERSHIP_EXTENSION_CLICK_EVENTS.SUB_SERVICES_SELECT_CLICK, {
        extension_sub_services: selectedSubServices,
        items_count: selectedSubServices?.length,
      });
    },
    [formHook],
  );

  // Save / Update / Cancel carry the whole form as it stands at click time, so a
  // dropped-off setup can be compared against a completed one.
  const getExtensionFormSnapshot = (): Partial<IClickLogBody> => ({
    extension_name: formHook.getValues("extension_name"),
    extension_category: formHook.getValues("category"),
    extension_amount: formHook.getValues("amount"),
    extension_days: MEMBERSHIP_EXTENSION_DEFAULT_DAYS,
    extension_status: toMembershipExtensionStatus(formHook.getValues("status")),
    extension_is_cart_search: formHook.getValues("is_cart_search"),
    extension_sub_services: formHook.getValues("sub_services")?.map((subService) => subService?.id) ?? [],
    extension_connected_memberships:
      formHook.getValues("connected_memberships")?.map((membership) => membership?.id) ?? [],
    extension_coupons: formHook.getValues("coupon_voucher_mappings") ?? [],
  });

  // Logged from the button click rather than from onSubmit so an attempt blocked
  // by validation is captured too.
  const handelSubmitClick = () => {
    logExtensionEvent(
      extension_id ? MEMBERSHIP_EXTENSION_CLICK_EVENTS.UPDATE_CLICK : MEMBERSHIP_EXTENSION_CLICK_EVENTS.SAVE_CLICK,
      { ...getExtensionFormSnapshot(), cta_text: extension_id ? "Update" : "Save" },
    );
  };

  const handelBack = () => {
    logExtensionEvent(MEMBERSHIP_EXTENSION_CLICK_EVENTS.CANCEL_CLICK, {
      ...getExtensionFormSnapshot(),
      cta_text: "Cancel",
    });
    navigate(getRouteKey("MEMBERSHIP_EXTENSION_LIST", "url"));
  };

  const onSubmit = (data: IMembershipExtensionFormProps) => {
    const mappings = data?.coupon_voucher_mappings ?? [];
    const selectedCoupons = data?.extension_coupons ?? [];

    // Coupons are optional, but a coupon that IS selected must carry a voucher
    // family. The per-row rule already blocks submit for rendered rows; this also
    // catches a coupon whose mapping row was never created (added while the sync
    // effect was still in flight), which would otherwise fall through silently.
    if (selectedCoupons.length > 0) {
      const hasUnmappedCoupon = selectedCoupons.some((coupon) => {
        const mapping = mappings.find((row) => row?.coupon_id === coupon?.coupon_id);
        return !mapping?.voucher_family_id;
      });
      if (hasUnmappedCoupon) {
        mappings.forEach((mapping, mappingIndex) => {
          if (mapping?.voucher_family_id) return;
          formHook.setError(`coupon_voucher_mappings.${mappingIndex}.voucher_family_id`, {
            type: "required",
            message: "Voucher family is required",
          });
        });
        return;
      }
    }

    const voucherByCouponId: Record<string, string | undefined> = {};
    mappings.forEach((m) => {
      voucherByCouponId[m.coupon_id] = m.voucher_family_id;
    });

    const subServiceIds = data?.sub_services?.map((subService) => subService?.id) ?? [];

    // Coupons are optional, so when none are selected the key is left out of the
    // payload altogether rather than sent as an empty array.
    const couponsDetailsPayload =
      selectedCoupons.length > 0
        ? {
            coupons_details: {
              exhaustible_coupons: selectedCoupons.map((coupon) => ({
                coupon_id: coupon?.coupon_id,
                voucher_family_id: voucherByCouponId[coupon?.coupon_id],
              })),
            },
          }
        : {};

    if (extension_id) {
      const constructedBody: IMembershipExtensionUpdateProps = {
        category: data?.category,
        category_id: data?.category_id,
        status: toMembershipExtensionStatus(data?.status),
        is_cart_search: Boolean(data?.is_cart_search),
        extension_days: MEMBERSHIP_EXTENSION_DEFAULT_DAYS,
        sub_service_ids: subServiceIds,
        sub_category_ids: subServiceIds,
        connected_memberships: data?.connected_memberships?.map((membership) => membership?.id) ?? [],
        ...couponsDetailsPayload,
      };
      updateMembershipExtension(constructedBody, extension_id).then(({ status }) => {
        if (status) {
          navigate(getRouteKey("MEMBERSHIP_EXTENSION_LIST", "url"));
        }
      });
    } else {
      const constructedBody: IMembershipExtensionPostBody = {
        extension_name: data?.extension_name,
        category: data?.category,
        category_id: data?.category_id,
        status: toMembershipExtensionStatus(data?.status),
        is_cart_search: Boolean(data?.is_cart_search),
        amount: parseFloat(data?.amount?.toString()),
        extension_days: MEMBERSHIP_EXTENSION_DEFAULT_DAYS,
        sub_service_ids: subServiceIds,
        sub_category_ids: subServiceIds,
        connected_memberships: data?.connected_memberships?.map((membership) => membership?.id) ?? [],
        ...couponsDetailsPayload,
      };
      createMembershipExtension(constructedBody).then(({ status }) => {
        if (status) {
          navigate(getRouteKey("MEMBERSHIP_EXTENSION_LIST", "url"));
        }
      });
    }
  };

  return (
    <div>
      <form className="w-full mt-2" onSubmit={formHook.handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-4 w-full bg-white px-4 py-3 sm:px-6 lg:px-8">
          <h2 className="text-sm font-semibold mt-3">Details</h2>
          <div className="flex items-center">
            <div className="w-2/12">
              <p>
                Name <span className="text-red-400">*</span>
              </p>
            </div>
            <div className="w-3/12">
              <Input
                className="text-xs"
                placeholder="Enter Name"
                disabled={extension_id && true}
                register={formHook.register("extension_name", {
                  required: true,
                  validate: (value) => value.trim() !== "" || "Name cannot be empty or only spaces",
                })}
                fieldError={formHook.formState.errors.extension_name}
                errorMessages={[
                  { message: "Name is required", type: "required" },
                  { message: "Name cannot be empty or only spaces", type: "validate" },
                ]}
                disableFieldError
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  formHook?.setValue("extension_name", e.target.value);
                  formHook.clearErrors("extension_name");
                }}
              />
            </div>
          </div>
          <div className="flex items-center mt-3">
            <div className="w-2/12">
              <p>
                Category <span className="text-red-400">*</span>
              </p>
            </div>
            <div className="w-3/12">
              <SearchableSelectMenu
                errorMessages={[{ message: "Category is required", type: "required" }]}
                onSelectItem={(item) => {
                  if (item) {
                    formHook.setValue("category", item.title);
                    formHook.setValue("category_id", item.id);
                    formHook.clearErrors("category");
                    logExtensionEvent(MEMBERSHIP_EXTENSION_CLICK_EVENTS.CATEGORY_SELECT_CLICK, {
                      extension_category: item.title,
                      event_action: item.id,
                    });
                  }
                }}
                fieldError={formHook.formState.errors.category}
                register={formHook.register("category", { required: true })}
                selectItems={membershipCategoryItems}
                placeholder="Select Category"
                showTooltips={true}
                showTypedErrors={false}
                showDropdownIcon={true}
                withDropDownSearch
                defaultSelected={membershipCategoryItems?.filter((oc) => oc.title === formHook.watch("category"))[0]}
                listBoxClassName="w-full"
                className="text-gray-800"
                containerClassName="w-full"
              />
            </div>
          </div>
          <div className="flex items-center mt-3">
            <div className="w-2/12">
              <p>
                Sub Services <span className="text-red-400">*</span>
              </p>
            </div>
            <div className="w-3/12">
              <MultiSelectDropdown
                data={subServiceItems}
                selectedKey="id"
                onSelectItem={handleSelectSubServices}
                defaultSelected={subServiceDefaultSelectedIds}
                required={true}
                deferRequiredError
                register={formHook.register}
                name="sub_service_ids"
                error={!!formHook.formState.errors.sub_service_ids}
                containerClassName="w-full"
              />
              {formHook.formState.errors.sub_service_ids && (
                <p className="mt-1 text-xs font-medium text-red-500">Sub Services is required</p>
              )}
            </div>
          </div>
          <div className="flex items-center mt-3">
            <div className="w-2/12">
              <p>
                Amount <span className="text-red-400">*</span>
              </p>
            </div>
            <div className="w-3/12">
              <Input
                className="text-xs"
                placeholder="Enter Amount"
                inputMode="decimal"
                disabled={extension_id && true}
                register={formHook.register("amount", {
                  required: true,
                  ...(forNumericWithDecimalAndNotStartFromZero.validations as any),
                })}
                fieldError={formHook.formState.errors.amount}
                errorMessages={[
                  { message: "Amount is required", type: "required" },
                  forNumericWithDecimalAndNotStartFromZero.errors,
                ]}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  formHook.setValue("amount", sanitizeAmountInput(e.target.value) as any, {
                    shouldValidate: true,
                  });
                }}
              />
            </div>
          </div>
          {/* Extension Days is hidden for now and sent as MEMBERSHIP_EXTENSION_DEFAULT_DAYS in the payload
          <div className="flex items-center mt-3">
            <div className="w-2/12">
              <p>
                Extension Days <span className="text-red-400">*</span>
              </p>
            </div>
            <div className="w-3/12">
              <Input
                className="text-xs"
                placeholder="Enter Extension Days"
                inputMode="numeric"
                register={formHook.register("extension_days", {
                  required: true,
                  ...(forExtensionDays.validations as any),
                })}
                fieldError={formHook.formState.errors.extension_days}
                errorMessages={[
                  { message: "Extension Days is required", type: "required" },
                  forExtensionDays.errors,
                  { message: "Extension Days cannot be more than 366", type: "maxLength" },
                ]}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  formHook.setValue("extension_days", sanitizeExtensionDaysInput(e.target.value) as any, {
                    shouldValidate: true,
                  });
                }}
              />
            </div>
          </div>
          */}
          <div className="flex items-center mt-3">
            <div className="w-2/12">
              <p>
                Connected Memberships <span className="text-red-400">*</span>
              </p>
            </div>
            <div className="w-3/12">
              <SearchableMultiSelectMenu
                onSelectItem={(items) => {
                  const selectedMemberships = items.map((i) => ({ id: i.id, name: i.title }));
                  formHook.setValue("connected_memberships", selectedMemberships);
                  formHook.clearErrors("connected_memberships");
                  logExtensionEvent(MEMBERSHIP_EXTENSION_CLICK_EVENTS.CONNECTED_MEMBERSHIPS_SELECT_CLICK, {
                    extension_connected_memberships: selectedMemberships,
                    items_count: selectedMemberships?.length,
                  });
                }}
                errorMessages={[{ message: "Connected Memberships is required", type: "required" }]}
                fieldError={formHook.formState.errors.connected_memberships}
                register={formHook.register("connected_memberships", {
                  required: true,
                })}
                withSelectAll
                showTooltips={true}
                showTypedErrors={false}
                placeholder="Select Memberships"
                defaultSelected={membershipItems.filter((m) =>
                  formHook.watch("connected_memberships")?.some((membership) => membership.id === m.id),
                )}
                selectItems={membershipItems}
              />
            </div>
          </div>
          <div className="flex items-center mt-3">
            <div className="w-2/12">
              <p>Status</p>
            </div>
            <div className="w-3/6 flex gap-6 items-center">
              <Switch
                checked={formHook.watch("status")}
                onChange={() => {
                  const nextStatus = !formHook.watch("status");
                  formHook.setValue("status", nextStatus);
                  logExtensionEvent(MEMBERSHIP_EXTENSION_CLICK_EVENTS.STATUS_TOGGLE_CLICK, {
                    extension_status: toMembershipExtensionStatus(nextStatus),
                  });
                }}
                className={`${formHook.watch("status") ? "bg-[#4D9FFF]" : "bg-[#CCCCCC]"}
          relative inline-flex h-6 w-14 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-white/75`}
              >
                <span
                  aria-hidden="true"
                  className={`${formHook.watch("status") ? "translate-x-8" : "translate-x-0"}
            pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out`}
                />
              </Switch>
            </div>
          </div>
          <div className="flex items-center mt-3">
            <div className="w-2/12">
              <p>Cart Search</p>
            </div>
            <div className="w-3/6 flex gap-6 items-center">
              <Switch
                checked={formHook.watch("is_cart_search")}
                onChange={() => {
                  const nextCartSearch = !formHook.watch("is_cart_search");
                  formHook.setValue("is_cart_search", nextCartSearch);
                  logExtensionEvent(MEMBERSHIP_EXTENSION_CLICK_EVENTS.CART_SEARCH_TOGGLE_CLICK, {
                    extension_is_cart_search: nextCartSearch,
                  });
                }}
                className={`${formHook.watch("is_cart_search") ? "bg-[#4D9FFF]" : "bg-[#CCCCCC]"}
          relative inline-flex h-6 w-14 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-white/75`}
              >
                <span
                  aria-hidden="true"
                  className={`${formHook.watch("is_cart_search") ? "translate-x-8" : "translate-x-0"}
            pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out`}
                />
              </Switch>
            </div>
          </div>
        </div>

        <div className="mt-2 flex flex-col gap-4 w-full bg-white px-4 py-3 sm:px-6 lg:px-8">
          <h2 className="text-sm font-semibold">Coupons for Exhaustible/Key Services</h2>
          <div className="flex items-center mt-3">
            <div className="w-2/12">
              <p>Coupons</p>
            </div>
            <div className="w-3/12">
              <SearchableMultiSelectMenu
                onSelectItem={(items) => {
                  const couponsId = items.map((i) => ({ coupon_id: i.id, coupon_name: i.title }));
                  formHook.setValue("extension_coupons", couponsId);
                  formHook.clearErrors("extension_coupons");

                  // Sync mapping rows with selected coupons
                  const existingMappings = formHook.getValues("coupon_voucher_mappings") ?? [];
                  const newMappings = couponsId.map((c) => {
                    const existing = existingMappings.find((m) => m.coupon_id === c.coupon_id);
                    return {
                      coupon_id: c.coupon_id,
                      coupon_name: c.coupon_name,
                      voucher_family_id: existing?.voucher_family_id,
                    };
                  });
                  replaceMappings(newMappings);
                  logExtensionEvent(MEMBERSHIP_EXTENSION_CLICK_EVENTS.COUPONS_SELECT_CLICK, {
                    extension_coupons: couponsId,
                    items_count: couponsId?.length,
                  });
                }}
                errorMessages={[]}
                register={formHook.register("extension_coupons", {
                  required: false,
                })}
                withSelectAll
                showTooltips={true}
                showTypedErrors={false}
                placeholder="Select Coupons"
                defaultSelected={exhaustibleCouponOptions.filter((c) =>
                  formHook.watch("extension_coupons")?.some((coupon) => coupon.coupon_id === c.id),
                )}
                selectItems={exhaustibleCouponOptions}
              />
            </div>
          </div>
        </div>
        {mappingFields?.length > 0 && (
          <div className="mt-2 flex flex-col gap-4 w-full bg-white px-4 py-3 sm:px-6 lg:px-8 border-t border-gray-100 pt-6">
            <h2 className="text-sm font-semibold">Voucher Family Mapping</h2>
            {mappingFields.map((field, index) => (
              <div key={field.id} className="flex items-center mt-3">
                <div className="w-2/12">
                  <p className="text-xs font-medium text-gray-700 truncate">
                    {formHook.watch(`coupon_voucher_mappings.${index}.coupon_name`)}
                  </p>
                </div>
                <div className="w-3/12">
                  {(() => {
                    const couponId = formHook.watch(`coupon_voucher_mappings.${index}.coupon_id`);
                    const allCoupons = membershipCouponList?.exhaustible_coupons ?? [];
                    const serviceType = allCoupons.find((ac) => ac.coupon_id === couponId)?.coupon_type;
                    const rowVoucherItems = reduceSelectMenu(
                      voucherFamiliesByServiceType[serviceType] && voucherFamiliesByServiceType[serviceType].length > 0
                        ? voucherFamiliesByServiceType[serviceType]
                        : voucherFamiliesByServiceType[VOUCHER_FALLBACK_KEY] ?? [],
                      "name",
                      "voucher_family_id",
                    );
                    return (
                      <SearchableSelectMenu
                        errorMessages={[{ message: "Voucher family is required", type: "required" }]}
                        onSelectItem={(item) => {
                          if (item) {
                            formHook.setValue(`coupon_voucher_mappings.${index}.voucher_family_id`, item.id);
                          } else {
                            formHook.setValue(`coupon_voucher_mappings.${index}.voucher_family_id`, undefined);
                          }
                          logExtensionEvent(MEMBERSHIP_EXTENSION_CLICK_EVENTS.VOUCHER_FAMILY_SELECT_CLICK, {
                            voucher_family_id: item?.id,
                            event_action: formHook.getValues(`coupon_voucher_mappings.${index}.coupon_id`),
                          });
                        }}
                        fieldError={
                          formHook?.formState?.errors?.coupon_voucher_mappings &&
                          (formHook?.formState?.errors?.coupon_voucher_mappings as any)[index]?.voucher_family_id
                        }
                        register={formHook.register(`coupon_voucher_mappings.${index}.voucher_family_id`, {
                          required: true,
                        })}
                        selectItems={rowVoucherItems}
                        placeholder="Select Voucher Family"
                        showTooltips={true}
                        showTypedErrors={false}
                        showDropdownIcon={true}
                        defaultSelected={rowVoucherItems?.find(
                          (oc) => oc.id === formHook.watch(`coupon_voucher_mappings.${index}.voucher_family_id`),
                        )}
                        listBoxClassName="w-full"
                        className="text-gray-800"
                        containerClassName="w-full"
                      />
                    );
                  })()}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="right-5 bottom-0 flex justify-end items-center gap-4 border-t-2 w-full p-3 bg-white">
          <p className="col-span-6"></p>
          <button
            type="button"
            onClick={() => handelBack()}
            className="text-xs mt-3 inline-flex w-full justify-center rounded-md bg-[#b1b5bb] px-4 py-3 font-semibold text-white ring-inset ring-gray-300 hover-bg-gray-50 sm:mt-0 sm:w-auto"
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handelSubmitClick}
            className="text-xs mt-3 inline-flex w-full justify-center rounded-md bg-[#193A6A] px-4 py-3 font-semibold text-white ring-inset ring-gray-300 hover-bg-gray-50 sm:mt-0 sm:w-auto"
          >
            {extension_id ? "Update" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default MembershipExtensionCreationComponent;
